import React, { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import Staff from "@/components/Staff";
import SentenceLines from "@/components/SentenceLines";
import JmlLink from "@/components/JmlLink";
import { MAJOR_INTERVALS } from "@/lib/hebrewCryptogram";
import { buildHebrewSequence } from "@/lib/hebrewComposition";
import { playSequenceAudio } from "@/lib/playEngine";
import { unlockAudio } from "@/lib/synth";

// The whole aleph-bet in order, aleph through tav — the method in one line.
const ALEF_BET = "אבגדהוזחטיכלמנסעפצקרשת";
const SCALE = { root: "G", modeIdx: 0, intervals: MAJOR_INTERVALS, baseOctave: 2 };
const STEP_MS = 500;

export default function AlefBetStaff({ compact = false }) {
  const [playing, setPlaying] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const stopRef = useRef(false);

  // Always on screen, fully written out — this is a specimen, not a puzzle.
  const sequence = useMemo(() => buildHebrewSequence([ALEF_BET], SCALE), []);

  const play = async () => {
    if (playing) return;
    unlockAudio();
    stopRef.current = false;
    setPlaying(true);
    await playSequenceAudio(sequence, {
      presetKey: "alefbet",
      voice: "pad",
      pedal: false,
      onTick: (idx) => {
        if (!stopRef.current) setActiveIdx(idx);
      },
    });
    setActiveIdx(-1);
    setPlaying(false);
  };

  return (
    <div data-testid="alefbet-demo">
      {!compact && (
        <p className="text-[11px] uppercase" style={{ color: "#F2D589", letterSpacing: "0.24em" }}>
          The map, in one line
        </p>
      )}
      {!compact && (
        <>
      <h2
        className="mt-6 max-w-[40ch]"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}
      >
        <SentenceLines text="Twenty-two letters. Twenty-two notes." />
      </h2>
      <p className="mt-6 max-w-[72ch] text-sm sm:text-base" style={{ color: "#C6BBA9", lineHeight: 1.7 }}>
        Aleph through tav, each letter on its own degree of the scale, climbing as
        the alphabet climbs. Every word you&apos;ll ever hear here is built out of
        these, in different scales and modes as Dan composes in real time.
      </p>
        </>
      )}

      <div className={compact ? "mt-2" : "mt-10"}>
        <Staff
          droppedEntries={sequence}
          activeIndex={activeIdx}
          baseStepMs={STEP_MS}
          showNoteNames={false}
        />
      </div>

      <motion.div className="mt-8 flex flex-wrap items-center gap-4">
        <button
          type="button"
          onClick={play}
          disabled={playing}
          data-testid="alefbet-play"
          className="inline-flex items-center justify-center rounded-full px-7 h-12 text-xs uppercase transition-colors hover:bg-[#F2D589]/[0.12] disabled:opacity-50"
          style={{
            backgroundColor: "transparent",
            color: "#F2D589",
            border: "1px solid #F2D589",
            letterSpacing: "0.2em",
            fontWeight: 500,
          }}
        >
          {playing ? "Playing" : "Play the aleph-bet"}
        </button>
        <JmlLink
          testId="alefbet-try-cta"
          className="inline-flex items-center justify-center rounded-full px-7 h-12 text-xs uppercase transition-colors hover:border-[#FCA07A] hover:text-[#FFD9C2]"
          style={{ border: "1px solid #241246", color: "#C6BBA9", letterSpacing: "0.2em", fontWeight: 500 }}
        >
          Play your own word at Jewish Music Lab
        </JmlLink>
      </motion.div>
    </div>
  );
}
